import { useEffect, useState } from "react";
// REDUX
import { useSelector } from "react-redux";
import { useGetBanksLightQuery } from "../../redux/banks/bankAPI";
// AUTHORIZATION
import GETJwtToken from "../../redux/authentication/authentication";
// INTERFACE
import { BankLightInterface } from "../../interfaces/Bank.interface";
import { RootState } from "../../app/store";

/**
 * Fetches the minimal bank projection via `useGetBanksLightQuery`.
 *
 * The light projection exists because the org switcher renders hundreds of banks
 * and the full one is far larger.
 *
 * Resolves a JWT first and passes it to the query, which stays skipped until the
 * token arrives. Scoped to the globally selected organisation unless overridden.
 */
export default function useGetAllBanksLight(overrideOrgId?: string) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [banks, setBanks] = useState<BankLightInterface[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [errorObject, setErrorObject] = useState<any>({});
  const [errorMessage, setErrorMessage] = useState<string>("Error");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { globalOrganizationId } = useSelector((state: RootState) => state.home);
  const orgId = overrideOrgId || globalOrganizationId;

  // ************************************************ */
  // FUNCTIONS ************************************** */

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const { data, isLoading, isError, isFetching, error } = useGetBanksLightQuery(
    { token, orgId },
    { skip: !token, refetchOnMountOrArgChange: false },
  );

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    GETJwtToken().then(setToken);
  }, []);

  useEffect(() => {
    if (data && data.data) {
      setBanks(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setErrorObject(error);
    }
  }, [error]);

  useEffect(() => {
    if (errorObject && errorObject.data && errorObject.data.message) {
      setErrorMessage(errorObject.data.message);
    }
  }, [errorObject]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return {
    banksLight: banks,
    isLoadingGetAllBanksLight: isLoading || isFetching,
    errorMessageGetAllBanksLight: errorMessage,
    isErrorGetAllBanksLight: isError,
    totalRowsGetAllBanksLight: data?.total ?? 0,
  };
}
